import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Inbox, Cpu } from 'lucide-react';
import api from '@/lib/api';
import { session } from '@/lib/session';
import AddSourceModal, { AdmissionBadge } from '@/components/AddSourceModal';

// Matches backend ROLE_PERMISSIONS["ingest:artifacts"] (auth/dependencies.py).
// Frontend role gating is UX only — backend RBAC remains authoritative.
const CAN_INGEST_ROLES = ['CANON_OWNER', 'KNOWLEDGE_STEWARD'];

const fmt = (d) => {
  if (!d) return 'N/A';
  try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return d; }
};

const STATUS_DOT = {
  'Current':       '#03f37c',
  'Review needed': '#a3a3a3',
  'Conflict':      '#000000',
  'Held':          '#d4d4d4',
  'Superseded':    '#d4d4d4',
};

const ALL_STATUSES = ['Current', 'Review needed', 'Conflict', 'Held', 'Superseded'];

// ─── KS2-I2 Extraction control (CANON_LOCKED only) ──────────────────────────

function ExtractionControl({ artifactId, admissionState }) {
  const [status, setStatus]   = useState('idle'); // idle | loading | success | error
  const [result, setResult]   = useState(null);
  const [errMsg, setErrMsg]   = useState('');

  // Extraction is only permitted for CANON_LOCKED artifacts.
  if (admissionState !== 'CANON_LOCKED') return null;

  async function handleExtract() {
    setStatus('loading');
    setErrMsg('');
    setResult(null);
    try {
      const r = await api.post(`/evidence/graph/extract/${artifactId}`);
      setResult(r.data);
      setStatus('success');
    } catch (err) {
      setErrMsg(err.response?.data?.detail || 'Extraction failed.');
      setStatus('error');
    }
  }

  return (
    <div className="mt-2" data-testid={`extraction-control-${artifactId}`}>
      {status === 'idle' && (
        <button
          data-testid={`extract-btn-${artifactId}`}
          onClick={handleExtract}
          className="inline-flex items-center gap-1 font-heading text-xs font-medium px-2.5 py-1 rounded border transition-colors border-gray-300 hover:border-gray-500 text-gray-600"
        >
          <Cpu size={11} />
          Extract candidates
        </button>
      )}

      {status === 'loading' && (
        <span className="font-mono text-xs text-gray-400" data-testid={`extraction-loading-${artifactId}`}>
          Extracting…
        </span>
      )}

      {status === 'success' && result && (
        <div
          className="text-xs border border-gray-200 rounded p-2 space-y-1 bg-gray-50"
          data-testid={`extraction-result-${artifactId}`}
        >
          <p className="text-gray-700">
            <strong>{result.claims_inserted}</strong> claim candidate{result.claims_inserted !== 1 ? 's' : ''},{' '}
            <strong>{result.constraints_inserted}</strong> constraint candidate{result.constraints_inserted !== 1 ? 's' : ''} extracted.
          </p>
          <p className="text-gray-500 italic" style={{ fontSize: '11px' }}>{result.note}</p>
          <Link
            to="/app/evidence"
            className="inline-block font-heading font-medium text-black underline"
            data-testid={`extraction-evidence-link-${artifactId}`}
          >
            View Evidence Graph →
          </Link>
        </div>
      )}

      {status === 'error' && (
        <p className="text-xs text-red-600" data-testid={`extraction-error-${artifactId}`}>
          {errMsg}
        </p>
      )}
    </div>
  );
}

// ─── Ingestion artifact row (queue) ─────────────────────────────────────────

function ArtifactRow({ art }) {
  return (
    <tr key={art.id} data-testid={`artifact-row-${art.id}`}>
      <td>
        <Link
          to={`/app/artifact/${art.id}`}
          className="font-heading font-medium text-black hover:underline"
          data-testid={`artifact-detail-link-${art.id}`}
        >
          {art.title}
        </Link>
      </td>
      <td className="font-body text-gray-600">{art.source_type}</td>
      <td className="font-heading text-gray-700 font-medium">
        {art.passage_count > 0 ? art.passage_count : '—'}
      </td>
      <td className="font-body text-gray-600">{art.owner}</td>
      <td className="font-body text-gray-500">{fmt(art.created_at)}</td>
      <td>
        <AdmissionBadge state={art.admission_state} />
        <ExtractionControl artifactId={art.id} admissionState={art.admission_state} />
      </td>
      <td style={{ textAlign: 'center' }}>
        <DualReviewPips approvals={art.approvals || []} />
      </td>
    </tr>
  );
}

function ArtifactCard({ art }) {
  return (
    <div key={art.id} className="ky-mobile-card-item" data-testid={`artifact-card-${art.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <Link
          to={`/app/artifact/${art.id}`}
          className="font-heading font-semibold text-black leading-snug hover:underline"
          style={{ fontSize: '15px' }}
          data-testid={`artifact-card-link-${art.id}`}
        >
          {art.title}
        </Link>
        <AdmissionBadge state={art.admission_state} />
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2">
        <div><div className="ky-mc-label">Type</div><div className="ky-mc-value text-gray-600">{art.source_type}</div></div>
        <div><div className="ky-mc-label">Owner</div><div className="ky-mc-value text-gray-600">{art.owner}</div></div>
        <div><div className="ky-mc-label">Submitted</div><div className="ky-mc-value text-gray-500">{fmt(art.created_at)}</div></div>
        {art.passage_count > 0 && (
          <div>
            <div className="ky-mc-label">Passages</div>
            <div className="ky-mc-value font-medium" data-testid={`passage-count-${art.id}`}>{art.passage_count}</div>
          </div>
        )}
      </div>
      <div className="mt-3">
        <DualReviewPips approvals={art.approvals || []} />
      </div>
      <ExtractionControl artifactId={art.id} admissionState={art.admission_state} />
    </div>
  );
}

function DualReviewPips({ approvals }) {
  const count = Math.min((approvals || []).length, 2);
  return (
    <span className="inline-flex items-center gap-1" title={`${count}/2 approvals`} data-testid="dual-review-pips">
      {[0, 1].map(i => (
        <span
          key={i}
          className="inline-block w-2 h-2 rounded-full"
          style={{ backgroundColor: i < count ? '#03f37c' : '#e5e5e5' }}
        />
      ))}
      <span className="font-heading text-gray-400 ml-1" style={{ fontSize: '10px' }}>
        {count}/2
      </span>
    </span>
  );
}

export default function Canon() {
  const [sources, setSources]         = useState([]);
  const [artifacts, setArtifacts]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [artLoading, setArtLoading]   = useState(true);
  const [error, setError]             = useState(null);
  const [filter, setFilter]           = useState('');
  const [showModal, setShowModal]     = useState(false);
  const [queueTab, setQueueTab]       = useState(false);

  const loadSources = (status) => {
    setLoading(true);
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    api.get(`/canon/sources${params}`)
      .then(r => setSources(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  };

  const loadArtifacts = () => {
    setArtLoading(true);
    api.get('/sources/artifacts')
      .then(r => setArtifacts(r.data))
      .catch(() => {})
      .finally(() => setArtLoading(false));
  };

  useEffect(() => { loadSources(''); loadArtifacts(); }, []);

  const applyFilter = (s) => { setFilter(s); loadSources(s); };

  const handleSuccess = () => {
    loadArtifacts();
    setQueueTab(true);
  };

  const canIngest = CAN_INGEST_ROLES.includes(session.getUser()?.role);

  return (
    <div data-testid="canon-page">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-1">
        <h1 className="page-heading">Canon</h1>
        {canIngest && (
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-1.5 font-heading font-semibold px-4 py-2 rounded transition-colors"
            style={{ backgroundColor: '#000', color: '#fff', fontSize: '13px' }}
            data-testid="add-source-btn"
          >
            <Plus size={14} />
            Add source
          </button>
        )}
      </div>
      <p className="font-body text-gray-500 mb-6" style={{ fontSize: '15px', lineHeight: 1.5 }}>
        The approved sources your organization relies on.
      </p>

      {/* Tab bar: Canon / Ingestion queue */}
      <div className="flex gap-0 mb-6 border-b" style={{ borderColor: '#e5e5e5' }}>
        <button
          onClick={() => setQueueTab(false)}
          className="font-heading font-semibold px-4 py-2 transition-colors"
          style={{
            fontSize: '13px',
            color: !queueTab ? '#000' : '#a3a3a3',
            borderBottom: !queueTab ? '2px solid #000' : '2px solid transparent',
            marginBottom: '-1px',
          }}
          data-testid="tab-canon"
        >
          Canon sources
        </button>
        <button
          onClick={() => setQueueTab(true)}
          className="font-heading font-semibold px-4 py-2 transition-colors relative"
          style={{
            fontSize: '13px',
            color: queueTab ? '#000' : '#a3a3a3',
            borderBottom: queueTab ? '2px solid #000' : '2px solid transparent',
            marginBottom: '-1px',
          }}
          data-testid="tab-queue"
        >
          Ingestion queue
          {artifacts.length > 0 && (
            <span
              className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full font-heading font-bold"
              style={{ backgroundColor: '#000', color: '#03f37c', fontSize: '9px' }}
              data-testid="queue-badge"
            >
              {artifacts.length}
            </span>
          )}
        </button>
      </div>

      {/* ── Canon sources tab ── */}
      {!queueTab && (
        <>
          {/* Status filter */}
          <div className="flex flex-wrap gap-2 mb-6" data-testid="canon-filter">
            <button
              onClick={() => applyFilter('')}
              className={`font-heading text-xs font-medium px-3 py-2 rounded border transition-colors ${filter === '' ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
              style={{ minHeight: '36px' }}
              data-testid="filter-all"
            >
              All
            </button>
            {ALL_STATUSES.map(s => (
              <button
                key={s}
                onClick={() => applyFilter(s)}
                className={`font-heading text-xs font-medium px-3 py-2 rounded border transition-colors ${filter === s ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                style={{ minHeight: '36px' }}
                data-testid={`filter-${s.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {s}
              </button>
            ))}
          </div>

          {loading && <p className="font-body text-gray-400 py-4">Loading sources…</p>}
          {error   && <p className="font-body text-gray-700 py-4">Error: {error}</p>}

          {!loading && !error && sources.length === 0 && (
            <p className="font-body text-gray-400 py-4">No sources found{filter ? ` with status "${filter}"` : ''}.</p>
          )}

          {!loading && !error && sources.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="ky-table-wrapper overflow-x-auto" data-testid="canon-sources-table">
                <table className="ky-table">
                  <thead>
                    <tr>
                      <th>Source</th><th>Type</th><th>Version</th><th>Owner</th>
                      <th>Last reviewed</th><th>Status</th><th style={{ textAlign: 'center' }}>Findings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sources.map((src) => (
                      <tr key={src.id} data-testid={`source-row-${src.id}`}>
                        <td>
                          <Link
                            to={`/app/canon/${src.id}`}
                            className="font-heading font-medium text-black hover:underline"
                            data-testid={`source-link-${src.id}`}
                          >
                            {src.title}
                          </Link>
                          {src.ingestion_artifact_id && (
                            <div className="mt-0.5">
                              <Link
                                to={`/app/artifact/${src.ingestion_artifact_id}`}
                                className="font-body text-gray-400 hover:text-black transition-colors"
                                style={{ fontSize: '11px' }}
                                data-testid={`source-artifact-link-${src.id}`}
                              >
                                ↳ originating artifact
                              </Link>
                            </div>
                          )}
                        </td>
                        <td className="font-body text-gray-600">{src.source_type}</td>
                        <td className="font-heading text-gray-700 font-medium">{src.active_version}</td>
                        <td className="font-body text-gray-600">{src.owner}</td>
                        <td className="font-body text-gray-500">{fmt(src.last_reviewed)}</td>
                        <td>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="status-dot" style={{ backgroundColor: STATUS_DOT[src.status] || '#a3a3a3' }} />
                            <span className="font-body text-gray-700" style={{ fontSize: '13px' }}>{src.status}</span>
                          </span>
                          {src.admission_state && src.admission_state !== 'CANON_LOCKED' && (
                            <span className="ml-2"><AdmissionBadge state={src.admission_state} /></span>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {src.open_findings > 0 ? (
                            <span className="font-heading font-semibold text-black" style={{ fontSize: '13px' }}>{src.open_findings}</span>
                          ) : (
                            <span className="font-body text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="ky-mobile-card" data-testid="canon-mobile-cards">
                {sources.map((src) => (
                  <div key={src.id} className="ky-mobile-card-item" data-testid={`source-card-${src.id}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <Link
                        to={`/app/canon/${src.id}`}
                        className="font-heading font-semibold text-black leading-snug"
                        style={{ fontSize: '15px' }}
                        data-testid={`source-link-mobile-${src.id}`}
                      >
                        {src.title}
                      </Link>
                      {src.open_findings > 0 && (
                        <span className="shrink-0 font-heading font-semibold text-black" style={{ fontSize: '13px' }}>
                          {src.open_findings} finding{src.open_findings !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-2">
                      <div><div className="ky-mc-label">Type</div><div className="ky-mc-value text-gray-600">{src.source_type}</div></div>
                      <div><div className="ky-mc-label">Version</div><div className="ky-mc-value font-medium">{src.active_version}</div></div>
                      <div><div className="ky-mc-label">Owner</div><div className="ky-mc-value text-gray-600">{src.owner}</div></div>
                      <div><div className="ky-mc-label">Reviewed</div><div className="ky-mc-value text-gray-500">{fmt(src.last_reviewed)}</div></div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <span className="status-dot" style={{ backgroundColor: STATUS_DOT[src.status] || '#a3a3a3' }} />
                      <span className="font-body text-gray-700" style={{ fontSize: '14px' }}>{src.status}</span>
                      {src.admission_state && src.admission_state !== 'CANON_LOCKED' && (
                        <AdmissionBadge state={src.admission_state} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* ── Ingestion queue tab ── */}
      {queueTab && (
        <>
          {artLoading && <p className="font-body text-gray-400 py-4">Loading queue…</p>}

          {!artLoading && artifacts.length === 0 && (
            <div className="text-center py-16" data-testid="empty-queue">
              <Inbox size={32} style={{ color: '#d4d4d4', margin: '0 auto 12px' }} />
              <p className="font-heading font-medium text-gray-400" style={{ fontSize: '14px' }}>
                No artifacts in the ingestion queue.
              </p>
              <p className="font-body text-gray-400 mt-1" style={{ fontSize: '13px' }}>
                Use "Add source" to submit a file for review.
              </p>
            </div>
          )}

          {!artLoading && artifacts.length > 0 && (
            <>
              {/* Desktop table */}
              <div className="ky-table-wrapper overflow-x-auto" data-testid="artifacts-table">
                <table className="ky-table">
                  <thead>
                    <tr>
                      <th>Title</th><th>Type</th><th>Version</th><th>Owner</th>
                      <th>Submitted</th><th>State</th><th style={{ textAlign: 'center' }}>Reviews</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifacts.map(art => <ArtifactRow key={art.id} art={art} />)}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="ky-mobile-card" data-testid="artifacts-mobile-cards">
                {artifacts.map(art => <ArtifactCard key={art.id} art={art} />)}
              </div>
            </>
          )}
        </>
      )}

      {/* Add source modal */}
      {showModal && (
        <AddSourceModal
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
